/**
 * Ported verbatim from `bimgo-ai-web`'s `lib/mentionUtils.ts` (item BI). The
 * grammar is a wire contract shared with chat history already written by the
 * old web and with the agent's starter prompts, so it is copied rather than
 * re-derived; the comments below are the reference's own. The only edits are
 * three `?? ""` guards for this repo's `noUncheckedIndexedAccess`.
 *
 * Mention marker grammar — wire-format embedded in user messages.
 *
 *   plain:  [[mention:type:id:label]]
 *   full:   [[mention:type:id:label:params:familyType]]
 *
 *     label      = the visible name, escaped via `encodeLabel` so the
 *                  three structurally ambiguous chars (`:`, `]`, `%`)
 *                  cannot be confused with the field separators or the
 *                  closing `]]`. All other chars (CJK, spaces, `<>`, `.`)
 *                  pass through untouched to keep marker text readable.
 *     params     = `pid~urlencoded(name)~scope` joined by `|`
 *                  (scope ∈ {i, t, f, l, b} = instance / type / family /
 *                  cad-layer / cad-block); empty when none selected.
 *     familyType = `urlencoded(FamilyName)~urlencoded(TypeName)`; empty
 *                  halves when only one side is known.
 *
 * Plain form is for reference-only types (Category / Level / View /
 * Room / Grid / RevitLinkInstance). All other types use the full form
 * with empty halves where applicable — groups 4 and 5 always emit as
 * a pair.
 *
 * `derivedFromType*` / `definition` / `storageType` are NOT in the
 * marker — they're re-derived at send time from the live cache via the
 * `enrich` lookup on `extractMentionMetadata`.
 */

import { requireDocumentContextElementId } from "./elementId";

export type MentionParameterScope =
  | "instance"
  | "type"
  | "family"
  | "layer"
  | "block";

export type MentionParameterDefinition = "BuiltIn" | "Shared" | "Project";

export type MentionParameterStorageType =
  | "Double"
  | "Integer"
  | "String"
  | "ElementId"
  | "None";

export interface MentionParameter {
  id: string;
  name: string;
  /** Optional on the type to tolerate malformed markers; production
   *  emission always sets one. */
  scope?: MentionParameterScope;
  /** Populated only when an enrichment lookup is supplied to
   *  `extractMentionMetadata`. */
  definition?: MentionParameterDefinition;
  /** Same enrichment caveat as `definition`. */
  storageType?: MentionParameterStorageType;
}

export interface MentionCadMetadata {
  sourcePath?: string;
  isLinked?: boolean;
  importUnitLabel?: string;
  importScale?: number;
  transformOrigin?: { x: number; y: number; z: number };
  linkZRotationDeg?: number;
}

export interface MentionMetadata {
  type: string;
  id: string;
  name: string;
  familyName?: string;
  typeName?: string;
  parameters?: MentionParameter[];
  /** CADLink mentions only: import units / scale / transform (from cache). */
  cadMetadata?: MentionCadMetadata;
}

export interface MentionListItem {
  type: string;
  id: string;
  /**
   * Omitted when `familyName` / `typeName` is set — those two already
   * convey the label, and carrying the concatenated string in addition
   * invites the agent to treat it as a separate filter. Kept for
   * reference-only types (Category, Level, View, Room, ...).
   */
  name?: string;
  familyName?: string;
  typeName?: string;
  order: number;
  parameters?: MentionParameter[];
  /** CADLink mentions only: import units / scale / transform. */
  cadMetadata?: MentionCadMetadata;
}

export const SKILL_MENTION_TYPE = "Skill";
export const SKILL_FOLDER_MENTION_TYPE = "SkillFolder";

export function isSkillMentionType(type: string): boolean {
  return type === SKILL_MENTION_TYPE || type === SKILL_FOLDER_MENTION_TYPE;
}

export type SkillMention =
  | { type: "skill"; id: string; label: string }
  | { type: "folder"; name: string; label: string };

export interface MentionExtractionResult {
  cleanContent: string;
  mentions: MentionMetadata[];
  skillMentions: SkillMention[];
  systemMessageBlock: string | null;
}

/**
 * Enrichment hook. When provided, `extractMentionMetadata` resolves each
 * parsed mention against the caller-supplied lookup and adds metadata
 * that the marker text does not carry (definition / storageType per
 * parameter). The shape mirrors what the DocumentContext hook stores in
 * `typeItemsByKey`, so MessageInput can pass a thin wrapper around its
 * cache.
 *
 * Implementations must be synchronous and side-effect-free (we run them
 * inside a regex `replace` callback).
 */
export interface MentionEnrichmentSource {
  /** Returns the cached DocumentContextItem-shaped record for one mention,
   *  or null/undefined when nothing is cached for the (type, id) pair. */
  lookup(
    type: string,
    id: string,
  ): EnrichmentItem | null | undefined;
  /**
   * Optional async cache warmer. `extractMentionMetadata` is synchronous and
   * only reads `lookup`, so callers must `await prefetch(type)` beforehand
   * for any type whose cache might be cold (e.g. CADLink, whose popover never
   * fetches parameter lists). Implementations must make the freshly fetched
   * items visible to a subsequent synchronous `lookup` in the same tick.
   */
  prefetch?(type: string): Promise<void>;
}

/** Subset of DocumentContextItem the enrichment path actually reads. */
export interface EnrichmentItem {
  typeParameters?: EnrichmentParameter[];
  instanceParameters?: EnrichmentParameter[];
  familyParameters?: EnrichmentParameter[];
  /** CADLink items carry import metadata we fold into the mention. */
  cadMetadata?: MentionCadMetadata;
}

export interface EnrichmentParameter {
  id: string | number;
  name?: string;
  definition?: MentionParameterDefinition;
  storageType?: MentionParameterStorageType;
}

/**
 * Regex matching the two legal marker forms in a single sweep.
 *
 * Capture groups:
 *   1. type
 *   2. id
 *   3. label                 (encoded via `encodeLabel`: `:`, `]`, `%`
 *                             arrive as `%3A` / `%5D` / `%25`, so the
 *                             only raw delimiter that can appear here
 *                             is the next field separator `:`)
 *   4. params payload        (empty when none selected)
 *   5. familyType payload    (empty when not applicable)
 *
 * Groups 4 and 5 are paired: either both present (full form) or both
 * absent (plain form). Group 4 forbids `:` so group 5 can be detected;
 * group 5 forbids `]` so the trailing `]]` is unambiguous. Group 3
 * tolerates `:` in legacy unescaped markers — the non-greedy quantifier
 * plus the `]]` anchor still pin the boundary deterministically.
 */
export const MENTION_PATTERN_SOURCE =
  "\\[\\[mention:([^:\\]]+):([^:\\]]+):([^\\]]+?)(?::([^\\]:]*):([^\\]]*))?\\]\\]";

const MENTION_PATTERN = new RegExp(MENTION_PATTERN_SOURCE, "g");

/**
 * Label-specific escape: percent-encode the three structurally ambiguous
 * chars (`:`, `]`, `%`) so the label can carry Revit's native naming
 * (e.g. `RevitLinkInstance` is `<file>.rvt : <id> : 位置 <未共用>`)
 * without colliding with field separators. CJK, spaces, `<>`, `.` are
 * deliberately preserved to keep the marker readable in logs.
 *
 * `%` must be encoded first so a literal `%3A` in the source label
 * round-trips to itself instead of decoding back to `:`. The single
 * `replace` pass guarantees one match per position, so the substitutions
 * never cascade.
 */
export function encodeLabel(label: string): string {
  return label.replace(/[%:\]]/g, (c) =>
    c === "%" ? "%25" : c === ":" ? "%3A" : "%5D",
  );
}

/**
 * Inverse of `encodeLabel`. Only recognises the three sequences the
 * encoder emits (`%25`, `%3A`, `%5D`) — any other `%XX` in the label
 * is treated as literal text. This is intentional: Revit element names
 * may contain `%` followed by digits (e.g. `25% scale`) and we must
 * never misread them as encoded chars.
 *
 * Legacy unescaped markers (no `%XX` at all) pass through unchanged,
 * so existing chat history rendered by this decoder is bit-identical
 * to the pre-fix behaviour.
 */
export function decodeLabel(raw: string): string {
  return raw.replace(/%(25|3A|5D)/gi, (_, hex: string) => {
    const upper = hex.toUpperCase();
    return upper === "25" ? "%" : upper === "3A" ? ":" : "]";
  });
}

/**
 * Single param-pair regex. Three segments separated by `~`:
 *   1. id  (no `~`)
 *   2. URL-encoded name (no `~`)
 *   3. scope marker — `i` instance / `t` type / `f` family / `l` cad-layer /
 *      `b` cad-block. Optional in the regex to keep the parser tolerant of
 *      stray malformed pairs; production emission always writes one.
 */
const PARAM_PAIR_PATTERN = /^([^~]+)~([^~]*)(?:~([itflb]))?$/;

function scopeToMarker(scope: MentionParameterScope | undefined): string {
  if (scope === "instance") return "~i";
  if (scope === "type") return "~t";
  if (scope === "family") return "~f";
  if (scope === "layer") return "~l";
  if (scope === "block") return "~b";
  return "";
}

function markerToScope(
  marker: string | undefined,
): MentionParameterScope | undefined {
  if (marker === "i") return "instance";
  if (marker === "t") return "type";
  if (marker === "f") return "family";
  if (marker === "l") return "layer";
  if (marker === "b") return "block";
  return undefined;
}

/**
 * Encode a list of {id, name, scope?} triples into the 5th marker group.
 * Always returns a string suitable to be appended after `:` (without leading
 * `:`). Empty input -> empty string so callers can drop the trailing colon.
 */
export function serializeParams(params: MentionParameter[] | undefined): string {
  if (!params || params.length === 0) return "";
  return params
    .map(
      (p) =>
        `${p.id}~${encodeURIComponent(p.name ?? "")}${scopeToMarker(p.scope)}`,
    )
    .join("|");
}

/**
 * Decode the 5th marker group back into a list of {id, name, scope?}
 * triples. Tolerant of malformed segments: a segment without `~` is treated
 * as an id-only mention with the same value used as the visible name
 * (best-effort fallback). A pair without a scope marker decodes with no
 * `scope` field, which is preserved by downstream consumers.
 */
export function parseParams(raw: string | undefined | null): MentionParameter[] {
  if (!raw) return [];
  return raw
    .split("|")
    .filter((seg) => seg.length > 0)
    .map((seg) => {
      const m = seg.match(PARAM_PAIR_PATTERN);
      if (!m) {
        return { id: seg, name: seg };
      }
      let decodedName = m[2] ?? "";
      try {
        decodedName = decodeURIComponent(m[2] ?? "");
      } catch {
        // Leave the raw value if it was not actually URI-encoded.
      }
      const scope = markerToScope(m[3]);
      const result: MentionParameter = { id: m[1] ?? seg, name: decodedName };
      if (scope) result.scope = scope;
      return result;
    });
}

/**
 * Encode a (familyName, typeName) pair into the 6th marker group. Returns
 * an empty string when both inputs are blank so callers can decide whether
 * to skip emitting the params/familyType pair entirely. When at least one
 * half is present we still emit both halves — the missing one is encoded
 * as the empty string — so the parser can rely on the `~` separator.
 */
export function serializeFamilyType(
  familyName: string | undefined | null,
  typeName: string | undefined | null,
): string {
  const fam = familyName ?? "";
  const typ = typeName ?? "";
  if (fam.length === 0 && typ.length === 0) return "";
  return `${encodeURIComponent(fam)}~${encodeURIComponent(typ)}`;
}

/**
 * Decode the 6th marker group back into a {familyName, typeName} pair.
 * Returns an empty object when the input is blank (plain-form markers);
 * empty halves decode to `undefined` so consumers don't have to repeat
 * the "is blank?" check.
 */
export function parseFamilyType(
  raw: string | undefined | null,
): { familyName?: string; typeName?: string } {
  if (!raw) return {};
  const idx = raw.indexOf("~");
  if (idx < 0) {
    let only = raw;
    try {
      only = decodeURIComponent(raw);
    } catch {
      // ignore
    }
    return only.length > 0 ? { familyName: only } : {};
  }
  const famRaw = raw.slice(0, idx);
  const typRaw = raw.slice(idx + 1);
  let fam = famRaw;
  let typ = typRaw;
  try {
    fam = decodeURIComponent(famRaw);
  } catch {
    // ignore
  }
  try {
    typ = decodeURIComponent(typRaw);
  } catch {
    // ignore
  }
  const result: { familyName?: string; typeName?: string } = {};
  if (fam.length > 0) result.familyName = fam;
  if (typ.length > 0) result.typeName = typ;
  return result;
}

// ---------------------------------------------------------------------------
// Clipboard helpers — used by chat history's copy button (`Message.tsx`)
// to convert raw `[[mention:...]]` markers into something usable when
// pasted (a) into external apps as readable text, or (b) back into the
// editor as proper chip nodes via TipTap's HTML clipboard parser.
// ---------------------------------------------------------------------------

/**
 * Replace markers with `@label: p1|p2` (or `@label` when no params).
 * Matches the editor's `clipboardTextSerializer` output so
 * copy-from-history and copy-from-editor produce identical plain text.
 */
export function humanizeMentionMarkers(content: string): string {
  const pattern = new RegExp(MENTION_PATTERN_SOURCE, "g");
  return content.replace(
    pattern,
    (_full, _type, _id, labelRaw: string, paramsRaw?: string) => {
      const label = decodeLabel(labelRaw);
      const params = parseParams(paramsRaw);
      if (params.length === 0) return `@${label}`;
      return `@${label}: ${params.map((p) => p.name).join("|")}`;
    },
  );
}

function escapeHtmlText(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildMentionSpanHtml(
  type: string,
  id: string,
  labelRaw: string,
  paramsRaw: string | undefined,
  familyTypeRaw: string | undefined,
): string {
  const decodedId = isSkillMentionType(type) ? decodeLabel(id) : id;
  const label = decodeLabel(labelRaw);
  const params = parseParams(paramsRaw);
  const { familyName, typeName } = parseFamilyType(familyTypeRaw);

  const attrs: string[] = [
    `class="revit-mention"`,
    `data-type="${escapeHtmlText(type)}"`,
    `data-id="${escapeHtmlText(decodedId)}"`,
    `data-label="${escapeHtmlText(label)}"`,
  ];
  if (familyName) attrs.push(`data-family-name="${escapeHtmlText(familyName)}"`);
  if (typeName) attrs.push(`data-type-name="${escapeHtmlText(typeName)}"`);
  if (params.length > 0) {
    // HTML-escape the JSON so embedded quotes survive paste parsing.
    attrs.push(`data-params="${escapeHtmlText(JSON.stringify(params))}"`);
  }

  // Mirror the plain-text humanization so HTML-only paste targets
  // see identical content.
  const paramsText =
    params.length > 0 ? `: ${params.map((p) => p.name).join("|")}` : "";
  const visible = `@${label}${paramsText}`;

  return `<span ${attrs.join(" ")}>${escapeHtmlText(visible)}</span>`;
}

/**
 * Marker-to-`<span class="revit-mention">` so paste round-trips via
 * `CustomMention.parseHTML`. Non-marker text is HTML-escaped verbatim
 * (we intentionally don't render Markdown here — that's a separate
 * concern and plain spans keep paste deterministic).
 */
export function buildMentionClipboardHtml(content: string): string {
  const pattern = new RegExp(MENTION_PATTERN_SOURCE, "g");
  const segments: string[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null = null;
  while ((match = pattern.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push(escapeHtmlText(content.slice(lastIndex, match.index)));
    }
    segments.push(
      buildMentionSpanHtml(
        match[1] ?? "",
        match[2] ?? "",
        match[3] ?? "",
        match[4],
        match[5],
      ),
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    segments.push(escapeHtmlText(content.slice(lastIndex)));
  }
  return segments.join("");
}

// Prompt sanitization: `derivedFromType*` is deliberately omitted from
// the wire payload. Leaking the sampled FamilySymbol caused agents to
// misuse it as a query filter (restricting collectors to that one symbol
// instead of all symbols of the Family). The popover still reads it
// from the live cache. Insertion order matters — JSON.stringify
// preserves it and the agent reads top-to-bottom.
function buildMentionArray(mentions: MentionMetadata[]): MentionListItem[] {
  return mentions.map((mention, index) => {
    const hasStructuredLabel = Boolean(mention.familyName || mention.typeName);
    return {
      ...(hasStructuredLabel ? {} : { name: mention.name }),
      ...(mention.familyName ? { familyName: mention.familyName } : {}),
      ...(mention.typeName ? { typeName: mention.typeName } : {}),
      type: mention.type,
      id: mention.id,
      order: index + 1,
      ...(mention.parameters && mention.parameters.length > 0
        ? { parameters: mention.parameters }
        : {}),
      ...(mention.cadMetadata ? { cadMetadata: mention.cadMetadata } : {}),
    };
  });
}

/**
 * Look up an enrichment parameter by id across the three layers. Returns
 * the first hit; the same parameter id should never appear twice across
 * layers because Revit guarantees unique BuiltIn ids and shared/project
 * params get distinct ElementIds.
 */
function findEnrichmentParam(
  item: EnrichmentItem | null | undefined,
  paramId: string,
): EnrichmentParameter | undefined {
  if (!item) return undefined;
  const layers: ReadonlyArray<EnrichmentParameter[] | undefined> = [
    item.instanceParameters,
    item.typeParameters,
    item.familyParameters,
  ];
  for (const layer of layers) {
    if (!layer) continue;
    const hit = layer.find((p) => String(p.id) === paramId);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Collect the distinct mention `type`s present in a raw message (the first
 * marker capture group). Used to decide which DocumentContext caches need
 * preheating before the synchronous `extractMentionMetadata` enrichment pass.
 */
export function collectMentionedTypes(content: string): Set<string> {
  const pattern = new RegExp(MENTION_PATTERN_SOURCE, "g");
  const types = new Set<string>();
  let match: RegExpExecArray | null = null;
  while ((match = pattern.exec(content)) !== null) {
    types.add(match[1] ?? "");
  }
  return types;
}

export function extractMentionMetadata(
  content: string,
  enrich?: MentionEnrichmentSource,
): MentionExtractionResult {
  const mentions: MentionMetadata[] = [];
  const skillMentions: SkillMention[] = [];

  // Use a fresh regex instance so the global state is not shared between
  // concurrent extractions in the same render pass.
  const pattern = new RegExp(MENTION_PATTERN_SOURCE, "g");

  const cleanContent = content.replace(
    pattern,
    (
      _match,
      type: string,
      id: string,
      labelRaw: string,
      paramsRaw?: string,
      familyTypeRaw?: string,
    ) => {
      const decodedId = isSkillMentionType(type) ? decodeLabel(id) : id;
      const label = decodeLabel(labelRaw);
      const parameters = parseParams(paramsRaw);
      const { familyName, typeName } = parseFamilyType(familyTypeRaw);

      if (type === SKILL_MENTION_TYPE) {
        skillMentions.push({ type: "skill", id: decodedId, label });
        return label;
      }
      if (type === SKILL_FOLDER_MENTION_TYPE) {
        skillMentions.push({ type: "folder", name: decodedId, label });
        return label;
      }
      const elementId = requireDocumentContextElementId(type, decodedId);

      // `derivedFromType*` is deliberately NOT enriched here — see the
      // sanitization policy above `buildMentionArray`. Failures are
      // silent so reload paths without a cache still produce a prompt.
      let enrichedParameters = parameters;
      let cadMetadata: MentionCadMetadata | undefined;
      if (enrich) {
        try {
          const item = enrich.lookup(type, elementId);
          if (item) {
            if (parameters.length > 0) {
              enrichedParameters = parameters.map((p) => {
                const src = findEnrichmentParam(item, p.id);
                if (!src) return p;
                const out: MentionParameter = { ...p };
                if (src.definition) out.definition = src.definition;
                if (src.storageType) out.storageType = src.storageType;
                return out;
              });
            }
            // CADLink import metadata travels even with no layer/block
            // selected, so the agent always has units/transform to work with.
            if (item.cadMetadata) cadMetadata = item.cadMetadata;
          }
        } catch {
          // Defensive: never let enrichment break the prompt path.
        }
      }

      mentions.push({
        type,
        id: elementId,
        name: label,
        ...(familyName ? { familyName } : {}),
        ...(typeName ? { typeName } : {}),
        ...(enrichedParameters.length > 0
          ? { parameters: enrichedParameters }
          : {}),
        ...(cadMetadata ? { cadMetadata } : {}),
      });

      // Inline-readable form for the agent's user-message text. The leading
      // `@` is stripped here because it's purely a UI affordance; carrying
      // it into the prompt is noise and can mislead the LLM into thinking
      // the user typed a literal `@`. The structured `id` / familyName /
      // typeName / parameters info still travels separately in the
      // ## Mentioned Revit Elements system block for reliable tool calling.
      if (parameters.length === 0) {
        return label;
      }
      const paramNames = parameters.map((p) => p.name).join("|");
      return `${label}: ${paramNames}`;
    },
  );

  if (mentions.length === 0) {
    return {
      cleanContent,
      mentions: [],
      skillMentions,
      systemMessageBlock: null,
    };
  }

  const mentionList = buildMentionArray(mentions);
  const systemMessageBlock =
    `## Mentioned Revit Elements\n` +
    `${JSON.stringify({ mentions: mentionList }, null, 2)}`;

  return {
    cleanContent,
    mentions,
    skillMentions,
    systemMessageBlock,
  };
}

// Re-exported for tests / external callers that need the raw regex without
// constructing a fresh instance.
export { MENTION_PATTERN };
